import { Component, OnInit, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription } from 'rxjs';
import { MachiningJournalService } from './machining-journal.service';
import { JournalEntry, MachinePhoto, OperationEntry, OperationType,
         LATHE_OPS, MILL_OPS, ALL_OPS, operationLabel } from './machining-journal.model';
import { MACHINING_MODULES } from './machining.data';
import { v4 as uuidv4 } from 'uuid';

@Component({
    selector: 'app-machining-journal',
    templateUrl: './machining-journal.component.html',
    styleUrls: ['./machining-journal.component.scss'],
    standalone: false
})
export class MachiningJournalComponent implements OnInit, OnDestroy {
  modules = MACHINING_MODULES;
  entries: JournalEntry[] = [];
  photosByEntry: { [entryId: string]: MachinePhoto[] } = {};

  userId: string | null = null;
  loading = true;

  // Streak
  streak = 0;
  daysSinceLastSession: number | null = null;

  // Form state (wired in Task 10)
  showForm = false;
  editingEntry: JournalEntry | null = null;
  formData: Partial<JournalEntry> = {};
  formError = '';
  saving = false;

  // File upload (wired in Task 11)
  selectedFiles: File[] = [];
  filePreviews: string[] = [];
  uploadProgress = 0;

  // Photo actions (wired in Task 11)
  lightboxPhoto: MachinePhoto | null = null;
  editingCaptionId: string | null = null;
  captionDraft = '';

  // Helpers exposed to template
  operationLabel = operationLabel;

  private authSub?: Subscription;
  private entriesSub?: Subscription;
  private photoSubs: Subscription[] = [];

  constructor(
    private auth: AngularFireAuth,
    private journalService: MachiningJournalService
  ) {}

  ngOnInit() {
    this.authSub = this.auth.user.subscribe(user => {
      this.userId = user?.uid ?? null;
      if (this.userId) {
        this.loadEntries();
      } else {
        this.loading = false;
      }
    });
  }

  ngOnDestroy() {
    this.authSub?.unsubscribe();
    this.entriesSub?.unsubscribe();
    this.photoSubs.forEach(s => s.unsubscribe());
  }

  private loadEntries() {
    if (!this.userId) return;
    this.entriesSub?.unsubscribe();
    this.entriesSub = this.journalService.getEntries(this.userId).subscribe(entries => {
      this.entries = entries;
      this.loading = false;
      this.calculateStreak();
      this.loadPhotosForEntries();
    });
  }

  private loadPhotosForEntries() {
    if (!this.userId) return;
    this.photoSubs.forEach(s => s.unsubscribe());
    this.photoSubs = [];
    this.photosByEntry = {};

    this.entries.forEach(entry => {
      const sub = this.journalService.getPhotosForEntry(this.userId!, entry.id).subscribe(photos => {
        this.photosByEntry = { ...this.photosByEntry, [entry.id]: photos };
      });
      this.photoSubs.push(sub);
    });
  }

  private calculateStreak() {
    const dates = [...new Set(this.entries.map(e => e.date))].sort().reverse();
    if (!dates.length) {
      this.streak = 0;
      this.daysSinceLastSession = null;
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const lastDate = new Date(dates[0] + 'T00:00:00');
    this.daysSinceLastSession = Math.floor((today.getTime() - lastDate.getTime()) / 86400000);

    const todayStr = today.toISOString().split('T')[0];
    const yesterdayStr = new Date(today.getTime() - 86400000).toISOString().split('T')[0];

    if (dates[0] !== todayStr && dates[0] !== yesterdayStr) {
      this.streak = 0;
      return;
    }

    let streak = 1;
    for (let i = 1; i < dates.length; i++) {
      const prev = new Date(dates[i - 1] + 'T00:00:00');
      const curr = new Date(dates[i] + 'T00:00:00');
      const diff = Math.round((prev.getTime() - curr.getTime()) / 86400000);
      if (diff === 1) streak++;
      else break;
    }
    this.streak = streak;
  }

  get streakLabel(): string {
    if (this.daysSinceLastSession === null) return 'No sessions yet — fire up the lathe.';
    if (this.daysSinceLastSession === 0) return `🔁 ${this.streak}-day streak — logged today!`;
    if (this.daysSinceLastSession === 1 && this.streak > 0) return `🔁 ${this.streak}-day streak — log today to keep it alive`;
    if (this.daysSinceLastSession === 1) return 'Last session: yesterday';
    return `Last session: ${this.daysSinceLastSession} days ago`;
  }

  getModuleTitle(moduleId: string | undefined): string {
    if (!moduleId) return '';
    return this.modules.find(m => m.id === moduleId)?.title ?? '';
  }

  formatDate(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  }

  getPhotos(entryId: string): MachinePhoto[] {
    return this.photosByEntry[entryId] || [];
  }

  // Form / operations / photo methods land in Tasks 10 + 11
  openNewForm() {
    this.editingEntry = null;
    const today = new Date().toISOString().split('T')[0];
    this.formData = { date: today, machine: 'lathe', operations: [] };
    this.formError = '';
    this.selectedFiles = [];
    this.filePreviews = [];
    this.showForm = true;
  }

  openEditForm(entry: JournalEntry) {
    this.editingEntry = entry;
    this.formData = {
      ...entry,
      operations: entry.operations ? entry.operations.map(op => ({ ...op })) : []
    };
    this.formError = '';
    this.selectedFiles = [];
    this.filePreviews = [];
    this.showForm = true;
  }

  closeForm() {
    this.showForm = false;
    this.editingEntry = null;
    this.formData = {};
    this.selectedFiles = [];
    this.filePreviews = [];
    this.uploadProgress = 0;
    this.saving = false;
  }

  get modulesForSelectedMachine() {
    const m = this.formData.machine;
    if (!m || m === 'both') return this.modules;
    return this.modules.filter(mod => mod.machine === m);
  }

  onMachineChange() {
    this.formData.moduleId = undefined;
  }

  get availableOpsForMachine(): OperationType[] {
    const m = this.formData.machine;
    if (m === 'lathe') return LATHE_OPS;
    if (m === 'mill')  return MILL_OPS;
    return ALL_OPS;
  }

  addOperation() {
    if (!this.formData.operations) this.formData.operations = [];
    const defaultType = this.availableOpsForMachine[0];
    this.formData.operations.push({ id: uuidv4(), type: defaultType });
  }

  removeOperation(index: number) {
    if (!this.formData.operations) return;
    this.formData.operations.splice(index, 1);
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;
    const incoming = Array.from(input.files);
    const room = Math.max(0, 10 - this.selectedFiles.length);
    const accepted = incoming.slice(0, room);
    this.selectedFiles = [...this.selectedFiles, ...accepted];
    accepted.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.filePreviews.push(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    });
    input.value = '';
  }

  removeSelectedFile(index: number) {
    this.selectedFiles.splice(index, 1);
    this.filePreviews.splice(index, 1);
  }

  async addPhotosToEntry(event: Event, entry: JournalEntry) {
    if (!this.userId) return;
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const files = Array.from(input.files).slice(0, 10);
    input.value = '';
    for (const file of files) {
      await this.journalService.uploadPhoto(this.userId, file, entry.id, entry.date);
    }
  }

  openLightbox(photo: MachinePhoto) {
    this.lightboxPhoto = photo;
  }

  closeLightbox() {
    this.lightboxPhoto = null;
    this.editingCaptionId = null;
  }

  startEditCaption(photo: MachinePhoto) {
    this.editingCaptionId = photo.id;
    this.captionDraft = photo.caption || '';
  }

  async saveCaption(photo: MachinePhoto) {
    if (!this.userId) return;
    await this.journalService.updatePhotoCaption(this.userId, photo.id, this.captionDraft);
    this.editingCaptionId = null;
    if (this.lightboxPhoto?.id === photo.id) {
      this.lightboxPhoto = { ...photo, caption: this.captionDraft };
    }
  }

  async deletePhoto(photo: MachinePhoto) {
    if (!this.userId) return;
    if (!confirm('Delete this photo?')) return;
    if (this.lightboxPhoto?.id === photo.id) this.closeLightbox();
    await this.journalService.deletePhoto(this.userId, photo);
  }

  async saveEntry() {
    if (!this.userId || !this.formData.date) {
      this.formError = 'Date is required.';
      return;
    }
    if (!this.formData.machine) {
      this.formError = 'Machine is required.';
      return;
    }

    this.saving = true;
    this.formError = '';

    try {
      let entryId: string;
      if (this.editingEntry) {
        // Edit path: write `null` for cleared optional fields so Firebase removes them.
        const updates = {
          id: this.editingEntry.id,
          createdAt: this.editingEntry.createdAt,
          date: this.formData.date,
          machine: this.formData.machine,
          moduleId:      this.formData.moduleId      || null,
          project:       this.formData.project       || null,
          material:      this.formData.material      || null,
          sessionLength: this.formData.sessionLength || null,
          operations:    (this.formData.operations && this.formData.operations.length) ? this.formData.operations : null,
          whatWentWell:  this.formData.whatWentWell  || null,
          challenges:    this.formData.challenges    || null,
          notes:         this.formData.notes         || null,
        } as any;
        entryId = this.editingEntry.id;
        await this.journalService.updateEntry(this.userId, updates);
      } else {
        // New-entry path: omit empty optional fields so they're not stored as null.
        const entryData: Omit<JournalEntry, 'id' | 'createdAt'> = {
          date: this.formData.date,
          machine: this.formData.machine,
          ...(this.formData.moduleId && { moduleId: this.formData.moduleId }),
          ...(this.formData.project && { project: this.formData.project }),
          ...(this.formData.material && { material: this.formData.material }),
          ...(this.formData.sessionLength && { sessionLength: this.formData.sessionLength }),
          ...(this.formData.operations && this.formData.operations.length && { operations: this.formData.operations }),
          ...(this.formData.whatWentWell && { whatWentWell: this.formData.whatWentWell }),
          ...(this.formData.challenges && { challenges: this.formData.challenges }),
          ...(this.formData.notes && { notes: this.formData.notes }),
        };
        entryId = await this.journalService.addEntry(this.userId, entryData);
      }

      if (this.selectedFiles.length > 0) {
        let uploaded = 0;
        for (const file of this.selectedFiles) {
          await this.journalService.uploadPhoto(this.userId, file, entryId, this.formData.date!);
          uploaded++;
          this.uploadProgress = Math.round((uploaded / this.selectedFiles.length) * 100);
        }
      }

      this.closeForm();
    } catch (err) {
      this.formError = 'Something went wrong saving your entry. Please try again.';
      this.saving = false;
    }
  }

  async deleteEntry(entry: JournalEntry) {
    if (!this.userId) return;
    const photos = this.photosByEntry[entry.id] || [];
    const confirmed = confirm(
      `Delete this session from ${this.formatDate(entry.date)}?` +
      (photos.length ? ` This will also delete ${photos.length} photo${photos.length > 1 ? 's' : ''}.` : '')
    );
    if (!confirmed) return;
    await this.journalService.deleteEntry(this.userId, entry, photos);
  }
}
